// AdminAgencies Component - Agencies and Invitations management
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Handshake, Mail, Plus, Trash2, Copy, CheckCircle, XCircle, 
  Send, Loader2, ExternalLink, Users, TrendingUp, Eye
} from 'lucide-react';
import { useAdmin } from './AdminContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAgencies({ 
  activeTab, 
  setActiveTab, 
  agencies: propAgencies, 
  invitations: propInvitations,
  adminKey,
  onRefreshAgencies,
  onRefreshInvitations,
  onSelectAgency
}) {
  // Always call useAdmin hook (required by React rules)
  const contextValues = useAdmin() || {};
  
  // Use props if provided, otherwise fallback to context
  const agencies = propAgencies || contextValues.agencies || [];
  const invitations = propInvitations || contextValues.invitations || [];
  const api = contextValues.api || axios.create({
    baseURL: `${API_URL}/api`,
    headers: { 'X-Admin-Key': adminKey }
  });
  const fetchAgencies = onRefreshAgencies || contextValues.fetchAgencies || (() => {});
  const fetchInvitations = onRefreshInvitations || contextValues.fetchInvitations || (() => {});
  const formatDate = contextValues.formatDate || ((d) => d ? new Date(d).toLocaleDateString('fr-CA') : 'N/A');
  const language = contextValues.language || 'fr';
  
  const [invitationDialog, setInvitationDialog] = useState(false);
  const [newInvitation, setNewInvitation] = useState({ email: '', agency_name: '', message: '' });
  const [creatingInvitation, setCreatingInvitation] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);

  const createInvitation = async () => {
    if (!newInvitation.email || !newInvitation.agency_name) {
      toast.error(language === 'fr' ? 'Email et nom d\'agence requis' : 'Email and agency name required');
      return;
    }
    setCreatingInvitation(true);
    try {
      await api.post('/agencies/invitations', newInvitation);
      toast.success(language === 'fr' ? 'Invitation envoyée!' : 'Invitation sent!');
      setInvitationDialog(false);
      setNewInvitation({ email: '', agency_name: '', message: '' });
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setCreatingInvitation(false);
    }
  };

  const cancelInvitation = async (invitationId) => {
    try {
      await api.delete(`/agencies/invitations/${invitationId}`);
      toast.success(language === 'fr' ? 'Invitation annulée' : 'Invitation cancelled');
      fetchInvitations();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const copyInvitationLink = (code) => {
    const link = `${API_URL}/agency/register?code=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(code);
    toast.success(language === 'fr' ? 'Lien copié!' : 'Link copied!');
    setTimeout(() => setCopiedLink(null), 3000);
  };

  const openAgencyPage = (slug) => {
    window.open(`${API_URL}/agency/${slug}`, '_blank');
  };

  // Render Agencies List
  if (activeTab === 'agencies' || activeTab === 'agencies-list') {
    return (
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg flex-shrink-0">
                <Handshake className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg sm:text-xl">
                  {language === 'fr' ? 'Agences Locales' : 'Local Agencies'}
                </CardTitle>
                <CardDescription className="text-sm">
                  {language === 'fr' ? 'Gérez les agences partenaires de votre réseau Québec+' : 'Manage partner agencies in your Québec+ network'}
                </CardDescription>
              </div>
            </div>
            <Button 
              onClick={() => setActiveTab('agencies-invitations')} 
              className="bg-gradient-to-r from-pink-500 to-rose-600 w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Inviter une agence' : 'Invite agency'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {agencies.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <div className="h-16 w-16 sm:h-20 sm:w-20 mx-auto rounded-2xl bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center mb-4">
                <Handshake className="h-8 w-8 sm:h-10 sm:w-10 text-pink-500" />
              </div>
              <h3 className="font-semibold text-lg mb-2">
                {language === 'fr' ? 'Aucune agence pour le moment' : 'No agencies yet'}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto text-sm sm:text-base">
                {language === 'fr' 
                  ? 'Commencez à construire votre réseau en invitant votre première agence locale partenaire.' 
                  : 'Start building your network by inviting your first local partner agency.'}
              </p>
              <Button onClick={() => setActiveTab('agencies-invitations')} className="bg-gradient-to-r from-pink-500 to-rose-600">
                <Mail className="h-4 w-4 mr-2" />
                {language === 'fr' ? 'Créer une invitation' : 'Create invitation'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {agencies.map((agency) => (
                <div 
                  key={agency.agency_id} 
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border hover:bg-muted/50 transition-colors gap-3 sm:gap-4"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center flex-shrink-0">
                      <Handshake className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium">{agency.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {agency.city}, {agency.region}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {agency.stats?.members || 0} {language === 'fr' ? 'membres' : 'members'}
                        </span>
                        <span className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          {agency.stats?.transactions || 0} tx
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      agency.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {agency.status === 'active' ? (language === 'fr' ? 'Active' : 'Active') : agency.status}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openAgencyPage(agency.slug)}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {language === 'fr' ? 'Voir la page' : 'View Page'}
                    </Button>
                    {onSelectAgency && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onSelectAgency(agency)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {language === 'fr' ? 'Détails' : 'Details'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Render Invitations
  if (activeTab === 'agencies-invitations') {
    return (
      <>
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg flex-shrink-0">
                  <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-lg sm:text-xl">
                    {language === 'fr' ? 'Invitations' : 'Invitations'}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {language === 'fr' ? 'Invitez de nouvelles agences à rejoindre votre réseau' : 'Invite new agencies to join your network'}
                  </CardDescription>
                </div>
              </div>
              <Button onClick={() => setInvitationDialog(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                {language === 'fr' ? 'Nouvelle Invitation' : 'New Invitation'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {invitations.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="h-16 w-16 sm:h-20 sm:w-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center mb-4">
                  <Send className="h-8 w-8 sm:h-10 sm:w-10 text-amber-500" />
                </div>
                <h3 className="font-semibold text-lg mb-2">
                  {language === 'fr' ? 'Aucune invitation' : 'No invitations'}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto text-sm sm:text-base">
                  {language === 'fr' 
                    ? 'Créez une invitation pour ajouter une nouvelle agence à votre réseau.' 
                    : 'Create an invitation to add a new agency to your network.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {invitations.map((inv) => (
                  <div 
                    key={inv.invitation_id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border hover:bg-muted/50 transition-colors gap-3 sm:gap-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        inv.status === 'pending' ? 'bg-amber-100 text-amber-600' :
                        inv.status === 'accepted' ? 'bg-green-100 text-green-600' :
                        inv.status === 'expired' ? 'bg-red-100 text-red-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {inv.status === 'accepted' ? <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6" /> :
                         inv.status === 'expired' ? <XCircle className="h-5 w-5 sm:h-6 sm:w-6" /> :
                         <Mail className="h-5 w-5 sm:h-6 sm:w-6" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium truncate">{inv.email}</h4>
                        <p className="text-sm text-muted-foreground truncate">
                          {inv.agency_name || (language === 'fr' ? 'Agence non spécifiée' : 'Agency not specified')}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            inv.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                            inv.status === 'accepted' ? 'bg-green-100 text-green-700' :
                            inv.status === 'expired' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {inv.status === 'pending' ? (language === 'fr' ? 'En attente' : 'Pending') :
                             inv.status === 'accepted' ? (language === 'fr' ? 'Acceptée' : 'Accepted') :
                             inv.status === 'expired' ? (language === 'fr' ? 'Expirée' : 'Expired') :
                             inv.status === 'cancelled' ? (language === 'fr' ? 'Annulée' : 'Cancelled') :
                             inv.status}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(inv.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {inv.status === 'pending' && (
                      <div className="flex items-center gap-2 sm:gap-3 justify-end sm:flex-shrink-0">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => copyInvitationLink(inv.code)}
                          className={`flex-1 sm:flex-none ${copiedLink === inv.code ? 'bg-green-50 border-green-300 text-green-700' : ''}`}
                        >
                          {copiedLink === inv.code ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              {language === 'fr' ? 'Copié!' : 'Copied!'}
                            </>
                          ) : (
                            <>
                              <Copy className="h-4 w-4 mr-2" />
                              {language === 'fr' ? 'Copier le lien' : 'Copy link'}
                            </>
                          )}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => cancelInvitation(inv.invitation_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New Invitation Dialog */}
        <Dialog open={invitationDialog} onOpenChange={setInvitationDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {language === 'fr' ? 'Nouvelle Invitation' : 'New Invitation'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{language === 'fr' ? 'Email du contact' : 'Contact Email'}</Label>
                <Input 
                  type="email"
                  value={newInvitation.email}
                  onChange={(e) => setNewInvitation({...newInvitation, email: e.target.value})}
                  placeholder="contact@agence.qc.ca"
                />
              </div>
              <div>
                <Label>{language === 'fr' ? 'Nom de l\'agence' : 'Agency Name'}</Label>
                <Input 
                  value={newInvitation.agency_name}
                  onChange={(e) => setNewInvitation({...newInvitation, agency_name: e.target.value})}
                  placeholder={language === 'fr' ? 'Caisse Locale de...' : 'Local Credit Union of...'}
                />
              </div>
              <div>
                <Label>{language === 'fr' ? 'Message personnalisé (optionnel)' : 'Custom Message (optional)'}</Label>
                <Textarea 
                  value={newInvitation.message}
                  onChange={(e) => setNewInvitation({...newInvitation, message: e.target.value})}
                  placeholder={language === 'fr' ? 'Bienvenue dans le réseau PayHub Québec+!' : 'Welcome to the PayHub Québec+ network!'}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInvitationDialog(false)}>
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </Button>
              <Button onClick={createInvitation} disabled={creatingInvitation}>
                {creatingInvitation ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                {language === 'fr' ? 'Envoyer l\'invitation' : 'Send Invitation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return null;
}
